import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useConnectApp } from '@Config/fetcher'
import useSwitchPanel from '@Config/fetcher/hooks/useSwitchPanel'
import useGetPanels from '@Config/fetcher/hooks/useGetPanels'
import useMe from '@Config/fetcher/hooks/useMe'
import { useRemoteAppActions, useRemoteAppState } from '@Store/slices/remoteApp'
import { useCacheActions, useCacheState } from '@Store/slices/cache'
import RouteAwareSkeleton from '@Shared/components/RouteAwareSkeleton'
import ErrorView from '@Shared/components/ErrorView'
import EmptyLayout from '@Shared/layouts/EmptyLayout'
import Sentry from '@Utils/Sentry'
import {
  clearStoredPanelId,
  readStoredPanelId,
} from '@Features/markets/storedPanel'

function AppConnectGuard({ children }: { children: JSX.Element }): JSX.Element {
  const location = useLocation()
  const { t: translate } = useTranslation()
  const { setAppUrl, setAppTokens } = useRemoteAppActions()
  const { accessToken } = useRemoteAppState()
  const { setRootMe, invalidate } = useCacheActions()
  const { rootMe } = useCacheState()

  const [
    connectApp,
    {
      data: connectAppData,
      isFinished: connectAppIsFinished,
      isError: connectAppIsError,
    },
  ] = useConnectApp()
  const [
    switchPanel,
    {
      data: switchData,
      isFinished: isSwitchFinished,
      isSuccess: isSwitchSuccess,
    },
  ] = useSwitchPanel()
  const [fetchPanels, { data: panels, isFinished: isPanelsFinished }] =
    useGetPanels()
  const [fetchMe, { data: meData, isSuccess: isMeSuccess }] = useMe()

  const [isBridgeReady, setIsBridgeReady] = useState(false)
  // Snapshot at mount: if there's no stored panel id, we don't need to wait
  // for the switch dance, so panelRestored starts true.
  const storedPanelId = useRef<number | null>(readStoredPanelId())
  const [panelRestored, setPanelRestored] = useState(
    storedPanelId.current === null,
  )

  const [bridgeError, setBridgeError] = useState(false)

  useEffect(() => {
    if (window.shopify?.ready) {
      window.shopify.ready.then(() => setIsBridgeReady(true))
    } else {
      // Bridge global unavailable (e.g. opened outside the Shopify iframe in
      // a regular browser tab). `useShopifyFetcher` throws synchronously when
      // `window.shopify` is missing, so we cannot fall through to `connectApp`
      // and proceed normally — that would silently park the user on the
      // skeleton forever. Flip an error flag and render a recoverable message
      // instead.
      setBridgeError(true)
    }
  }, [])

  const fetchURL = `${import.meta.env.VITE_APP_URL}/shopify/app${location.search}`

  useEffect(() => {
    if (!isBridgeReady) return
    connectApp(fetchURL, 'POST')
  }, [isBridgeReady]) // eslint-disable-line react-hooks/exhaustive-deps

  // Once connectApp returns the app URL, the appUrlMiddleware decodes the
  // embedded token and dispatches setAppTokens. We just hand off the URL.
  useEffect(() => {
    if (!connectAppData) return
    const { redirect, url } = connectAppData
    if (redirect) {
      open(url, '_top')
      return
    }
    if (url) {
      setAppUrl(url)
    }
  }, [connectAppData]) // eslint-disable-line react-hooks/exhaustive-deps

  // After tokens land (via middleware), pre-fetch the root /users/me through
  // the standard useMe path (which honours 401-refresh) so rootMe is seeded
  // before any subsequent panel switch. Skip if we have no stored panel to
  // restore — rootMe will be seeded lazily by PanelSwitcher otherwise.
  useEffect(() => {
    if (!accessToken) return
    if (storedPanelId.current === null) return
    if (rootMe) return
    fetchMe(false)
  }, [accessToken]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMeSuccess && meData && !rootMe) {
      setRootMe(meData)
    }
  }, [isMeSuccess, meData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Once rootMe is seeded, validate the stored panel id against the live panel
  // list before switching. Drops the stored value if it's stale or tampered.
  useEffect(() => {
    if (!rootMe) return
    if (storedPanelId.current === null) return
    fetchPanels()
  }, [rootMe]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isPanelsFinished) return
    if (storedPanelId.current === null) return

    const panelIds = (panels ?? []).map((p) => p.company_id)
    const targetId = storedPanelId.current
    if (panelIds.includes(targetId)) {
      switchPanel(targetId)
    } else {
      Sentry.message(
        `[AppConnectGuard] Stored panel ${targetId} not in panel list — dropping.`,
        'warning',
      )
      clearStoredPanelId()
      storedPanelId.current = null
      setPanelRestored(true)
    }
  }, [isPanelsFinished]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSwitchFinished) return
    if (isSwitchSuccess && switchData) {
      setAppTokens(switchData)
      // The root /users/me prefetch above seeded cache.me with the ROOT
      // panel's data. Now that we've swapped to the child panel's tokens,
      // drop that cached value so downstream `useMe(true)` consumers refetch
      // the active panel's me instead of reusing the root one. (rootMe is
      // set-once and intentionally preserved for the panel switcher.)
      invalidate(['me'])
    } else {
      // Switch failed (e.g. panel deactivated since the id was stored, or a
      // transient backend error). We can't recover automatically, so drop
      // the stored panel id and fall back to root tokens; a stale stored
      // panel would otherwise loop the user through this failure on every
      // load. Log so we notice if this becomes common.
      Sentry.message(
        '[AppConnectGuard] Panel switch failed — falling back to root tokens.',
        'warning',
      )
      clearStoredPanelId()
      storedPanelId.current = null
    }
    setPanelRestored(true)
  }, [isSwitchFinished]) // eslint-disable-line react-hooks/exhaustive-deps

  if (bridgeError) {
    // Recoverable message; the merchant likely opened the URL outside of the
    // Shopify admin iframe. Tell them to open the app from Shopify admin.
    return (
      <EmptyLayout>
        <ErrorView
          title={translate('Prisync.ERRORS.bridgeUnavailableTitle')}
          message={translate('Prisync.ERRORS.bridgeUnavailableMessage')}
        />
      </EmptyLayout>
    )
  }

  if (!connectAppIsFinished || !panelRestored) {
    return <RouteAwareSkeleton />
  }

  // `useShopifyFetcher` records HTTP failures in `meta.isError` without
  // throwing, so without this check the guard would fall through to
  // `children` with no tokens — and downstream `ChargeGuard` would fail open
  // on a backend error. Surface the error to the merchant instead.
  if (connectAppIsError || (!connectAppData && connectAppIsFinished)) {
    return (
      <EmptyLayout>
        <ErrorView
          title={translate('Prisync.ERRORS.title')}
          message={translate('Prisync.ERRORS.default')}
        />
      </EmptyLayout>
    )
  }

  if (connectAppData?.redirect) {
    return <></>
  }

  return children
}

export default AppConnectGuard
